import React, { lazy, Suspense, useCallback, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import NoDataComponent from '../../no_data_component.jsx';
import { useChartOptions } from '../../hooks/useChartOptions.js';
import { useDatasourceData } from '../../hooks/useDatasourceData.js';
import SkeletonGrid from '../../../../skeleton_loaders/common/SkeletonGrid.jsx';
import { cloneDeep } from 'lodash';
import { useDatasource } from '../../hooks/useDatasource.js';
import { useFormulaFields } from '../../hooks/useFormulaFields.js';
const AgChart = lazy(() => import('../../../common/AgGrid/AgChart.jsx'));

const EXCLUDED_KEYS = ['typeId', 'name', 'excludedSettings', 'colorMode'];
const EXCLUDED_REPEATED_COLORS = ['fills', 'strokes']

function CustomChartWidget({widgetId, widgetRef, theme}) {
  const chartRef = useRef();
  const { chartOptions } = useChartOptions({widgetId});
  const series = useSelector(state => state.dashboard.widgets[widgetId].settings?.series || []);
  const { rows: filteredRows, cols } = useDatasourceData(widgetId);
  const { applyFormulas } = useFormulaFields(widgetId);
  
  useDatasource({ widgetId, live: true });
  
  const normalizeValue = useCallback((value) => {
    if (value instanceof Date) return value;

    if (value && typeof value === 'object' && typeof value.label === 'string') {
      if (value.id != null) {
        return `${value.label} (${value.id})`;
      }
      if (value.value != null) {
        return `${value.label} (${value.value})`;
      }
      return value.label;
    }

    return value;
  }, []);

  const deepClean = useCallback((value, excluded = []) => {
    if (Array.isArray(value)) return value.map(v => deepClean(v, excluded));
    if (value && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value)
          .filter(([k, v]) => !excluded.includes(k) && v !== null && v !== '')
          .map(([k, v]) => [k, deepClean(v, excluded)])
          .filter(([_, v]) => !(v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0))
      );
    }
    return value;
  }, []);

  const filteredChartOptions = useMemo(() => deepClean(chartOptions), [chartOptions, deepClean]);

  const { chartTypeId, axes, ...rest } = filteredChartOptions;
  const filteredSeries = useMemo(() => series.map(item => {
    const newItem = cloneDeep(item);
    const multipleColors = newItem.colorMode === 'multiple'
    if (multipleColors){
      newItem.itemStyler = (params) => {
        const colors = newItem.fills || [];
        const strokes = newItem.strokes || [];
        return {
          fill: colors[params.datum.index % colors.length],
          stroke: strokes[params.datum.index % strokes.length]
        }
      }
      if (newItem.fills?.length > 1 || newItem.strokes?.length > 1){
        // Currently the default color go with the legend, so if series has multiple line/bar,... then show in legend as single attribute by default will confuse user
        newItem.showInLegend = false;
      }
    }
    return deepClean(newItem, [...EXCLUDED_KEYS, ...(newItem.excludedSettings || []), ...(multipleColors ? EXCLUDED_REPEATED_COLORS : [])])
  }, [series, EXCLUDED_KEYS, deepClean]));

  const timeAxesKeys = useMemo(() => {
    return axes
      ?.filter(a => a.type === 'time')
      .flatMap(a => a.keys || []);
  }, [axes]);

  const selectionLabelByAccessor = useMemo(() => {
    const map = {};
    for (const col of cols ?? []) {
      if (col?.accessor && Array.isArray(col.selection_options)) {
        const m = new Map();
        for (const opt of col.selection_options) {
          m.set(String(opt.value), opt.label);
        }
        map[col.accessor] = m;
      }
    }
    return map;
  }, [cols]);

  const formattedRows = useMemo(() => {
    const indexRows = filteredRows.map((r, index) => {
      // Formulas use the raw (already filtered/pivoted) values, before label formatting
      const out = { index, ...applyFormulas(r) };
      for (const k in r) {
        let v = normalizeValue(r[k]);
        const lookup = selectionLabelByAccessor[k];
        if (lookup) {
          const key = v == null ? null : String(v);
          if (key != null && lookup.has(key)) {
            v = lookup.get(key);
          }
        }
        out[k] = v;
      }
      return out;
    });

    if (!timeAxesKeys?.length) return indexRows;
    return indexRows.map(r => {
      const copy = { ...r };
      for (const k of timeAxesKeys) {
        if (copy[k] != null && !(copy[k] instanceof Date)) {
          copy[k] = new Date(copy[k]);
        }
      }
      return copy;
    });
  }, [filteredRows, timeAxesKeys, selectionLabelByAccessor, applyFormulas]);

  const options = useMemo(() => ({
    theme: deepClean(theme),
    data: formattedRows,
    series: filteredSeries,
    ...rest,
    ...(!['donut', 'pie'].includes(chartTypeId) && {axes: axes})
  }), [formattedRows, filteredChartOptions, filteredSeries, theme]);

  if (!chartOptions || !series) return <NoDataComponent />;
  
  return (
    <div style={{ height: '100%', width: '100%', position: 'relative' }}>
      <Suspense fallback={<SkeletonGrid />}>
        <AgChart options={options} ref={chartRef} style={{ position: 'absolute', width: '100%', height: '100%', overflow: 'hidden' }} />
      </Suspense>
    </div>
  )
}

export default CustomChartWidget;
